import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Subscription, combineLatest, takeUntil } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { PUBLIC_HOLIDAYS } from './../../../../core/data/app.data';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { PatientTaskService } from './../../../../core/services/patient-task.service';
import { PatientService } from './../../../../core/services/patient.service';
import { ScheduleService } from './../../../../core/services/schedule.service';
import { TaskService } from './../../../../core/services/task.service';
import { taskAssigneeSummary } from './../../../../core/utils/task-assignees';
import { allDayBars, dayPreview, monthCells } from './../../../../core/utils/month-grid';

/** Rows of bars and task lines a month cell holds before "+n more". */
const MONTH_CELL_ROWS = 7;

@Component({
  selector: 'app-task-calendar',
  templateUrl: './task-calendar.component.html',
  styleUrls: ['./task-calendar.component.scss'],
  animations: [
    trigger('rowAnimation', [
      // Enter transition
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'scaleY(0)',
          transformOrigin: 'top center'
        }),
        animate('0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)', style({
          opacity: 1,
          transform: 'scaleY(1)'
        })),
      ]),
      // Leave transition
      transition(':leave', [
        animate('0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)', style({
          opacity: 0,
          transform: 'scaleY(0)',
          transformOrigin: 'top center'
        }))
      ])
    ])
  ],
})
export class TaskCalendarComponent extends BaseComponent implements OnInit, OnDestroy {

  tasks: any[] | null = null;
  readonly assigneeSummary = taskAssigneeSummary;
  tasksForSelectedDay: any[] | null = null;

  currentMonth = new Date();
  dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  days: any[] = [];
  months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  selectedMonth: number = new Date().getMonth();
  selectedYear: number = new Date().getFullYear();
  selectedDay: Date | null = null;
  startOfWeekIndex: number | null = null;

  availableDays: string[] = [];

  private loadByMonthSubscription?: Subscription
  private patientsSubscription?: Subscription;

  /* TODO: dynamically load this from somewhere online to ensure frequent updates */
  private publicHolidays = PUBLIC_HOLIDAYS;

  statusColorMap: { [key: string]: { backgroundColor: string, textColor: string, borderColor: string } } = {
    'Pending': { backgroundColor: 'orange', textColor: '#fff', borderColor: 'orange' },
    'Completed': { backgroundColor: 'green', textColor: '#fff', borderColor: 'green' },
    'Cancelled': { backgroundColor: 'red', textColor: '#fff', borderColor: 'red' }
  };

  constructor(
    private taskService: TaskService,
    private patientTaskService: PatientTaskService,
    private patientService: PatientService,
    private scheduleService: ScheduleService,
    private router: Router,
    private cd: ChangeDetectorRef,
    dialog: MatDialog,
    fs: FeedbackService) {
    super(fs, dialog);
  }

  ngOnInit(): void {
    combineLatest([
      this.scheduleService.schedule$,
      this.taskService.monthTasks$,
    ]).pipe(
      takeUntil(this.destroy$)
    ).subscribe(([settings, tasks]) => {
      if (settings) {
        this.availableDays = settings.availableDays;
      }
      if (tasks) {
        this.tasks = this.sortTasks([
          ...(tasks || []),
        ]);
        this.updateTasksForSelectedDay();
      }

      this.generateCalendar();
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();

    if (this.loadByMonthSubscription) {
      this.loadByMonthSubscription.unsubscribe();
    }

    if (this.patientsSubscription) {
      this.patientsSubscription.unsubscribe();
    }
  }

  private sortTasks(tasks: any[] | null): any[] | null {
    if (!tasks?.length) {
      return tasks;
    }
    const sorted = _.chain(tasks)
      .flatMap(task => {
        if (task.dueDate) {
          const colors = this.statusColorMap[task.status] || { backgroundColor: '#fff', textColor: '#000', borderColor: '#fff' };
          return {
            ...task,
            typeDetails: { type: task.status, ...colors }
          };
        }
        return []; // Return an empty array if the task has no dueDate to avoid undefined values
      })
      .sortBy(task => task.task)
      .value()
      .reverse();

    return sorted;
  }


  onMonthYearChange(): void {
    this.currentMonth = new Date(this.selectedYear, this.selectedMonth);
    this.generateCalendar();
    this.loadTasksForMonth();
  }


  trackByDate(index: number, day: any): any {
    return day.date;
  }

  resetOpenRows() {
    if (!this.selectedDay) return
    this.selectedDay = null;
    this.tasksForSelectedDay = null;
    this.startOfWeekIndex = null;
  }

  generateCalendar(): void {
    const cells = monthCells(this.currentMonth);
    const first = moment(cells[0].date).startOf('day');
    const last = moment(cells[cells.length - 1].date).endOf('day');
    const holidays = (this.publicHolidays ?? [])
      .filter((h: any) => moment(h.date).isBetween(first, last, 'day', '[]'))
      .map((h: any) => ({ id: `holiday-${h.date}`, start: moment(h.date).toDate(), end: moment(h.date).toDate(), title: h.name, holiday: true }));
    const bars = allDayBars(holidays, cells.map(c => c.date));
    const todayKey = moment().format('YYYY-MM-DD');

    this.days = cells.map((cell, i) => {
      const shown = { number: cell.date.getDate(), inMonth: cell.inMonth, bars: bars[i] };
      // Days of the months either side are shown faded and cannot be opened.
      if (!cell.inMonth) {
        return { ...shown, date: null, taskCounts: [], preview: null, isToday: false, isAvailable: false };
      }
      // Same order as the day list: by task text.
      const dayTasks = _.sortBy((this.tasks ?? []).filter(t => moment(t.dueDate).isSame(cell.date, 'day')), t => t.task);
      return {
        ...shown,
        date: cell.date,
        taskCounts: this.getTaskCountsForDate(cell.date),
        // Tasks have no time; each line is the patient and the task, in the status colour.
        preview: dayPreview(dayTasks.map(t => ({
          timeSlot: '',
          reason: [t.Patient ? [t.Patient.surname, t.Patient.name].filter(Boolean).join(', ') : '', t.task].filter(Boolean).join(' · '),
          typeDetails: t.typeDetails,
          isCancelled: t.status === 'Cancelled',
        })), MONTH_CELL_ROWS - bars[i].length),
        isToday: moment(cell.date).format('YYYY-MM-DD') === todayKey,
        isAvailable: this.isAvailable(cell.date),
      };
    });
  }

  goToToday(): void {
    const today = new Date();
    this.selectedMonth = today.getMonth();
    this.selectedYear = today.getFullYear();
    this.currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    this.generateCalendar();
    this.loadTasksForMonth();
  }

  private getTaskCountsForDate(date: Date): any[] {
    if (!this.tasks) return [];

    const targetDate = moment(date).startOf('day');
    const groupedByType: { [key: string]: { count: number, details: { type: string, backgroundColor: string, textColor: string, borderColor: string } } } = {};



    this.tasks.filter(task => task.dueDate).forEach(task => {
      const dueDate = moment(task.dueDate).startOf('day');
      if (dueDate.isSame(targetDate, 'day')) {
        const type = task.status;
        if (!groupedByType[type]) {
          const colors = this.statusColorMap[type] || { backgroundColor: '#fff', textColor: '#000', borderColor: '#fff' };
          groupedByType[type] = { count: 0, details: { type, ...colors } };
        }
        groupedByType[type].count++;
      }
    });

    return Object.keys(groupedByType).map(type => ({
      type: type,
      count: groupedByType[type].count,
      backgroundColor: groupedByType[type].details.backgroundColor || '#202123',
      textColor: groupedByType[type].details.textColor || '#202123',
      borderColor: groupedByType[type].details.borderColor || groupedByType[type].details.backgroundColor
    }));
  }


  hasHoliday(date: Date): boolean {
    if (!date) return false;
    const dateMoment = moment(date).startOf('day');
    return this.publicHolidays?.some(holiday =>
      dateMoment.isSame(moment(holiday.date).startOf('day'), 'day')
    ) || false;
  }

  getHoliday(date: Date): any {
    const holiday = this.publicHolidays.find(holiday =>
      moment(date).isSame(moment(holiday.date), 'day')
    );
    return holiday || null;
  }

  getPublicHolidayDetails = _.memoize((date: Date) => {
    if (!this.hasHoliday(date)) {
      return null;
    }
    return this.getHoliday(date);
  }, date => moment(date).format('YYYY-MM-DD'));


  isToday(date: Date): boolean {
    const today = new Date();
    return date?.getDate() === today.getDate() &&
      date?.getMonth() === today.getMonth() &&
      date?.getFullYear() === today.getFullYear();
  }

  isAvailable(date: Date): boolean {
    if (!date) return false;
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });

    return this.availableDays.includes(dayName); // Date is available
  }

  isFutureDate(date: Date) {

    const dateMoment = moment(date);
    // Check if date is in the past
    if (!dateMoment.isAfter(moment().subtract(1, 'days'))) {
      return false;
    }

    return true;
  }

  navigate(monthIncrement: number): void {
    let newMonth = new Date(this.currentMonth);
    // Set date to 1 before changing months
    newMonth.setDate(1);
    newMonth.setMonth(newMonth.getMonth() + monthIncrement);
    this.currentMonth = new Date(newMonth);
    this.generateCalendar();
    this.loadTasksForMonth();
  }


  loadTasksForMonth() {
    if (this.loadByMonthSubscription) {
      this.loadByMonthSubscription.unsubscribe();
    }

    this.loadByMonthSubscription =

      this.taskService.loadTasksByMonth(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1)
        .subscribe();
  }


  selectDay(selectedDay: any): void {
    // Days outside the month have no date and do not open.
    if (!selectedDay.date) return;

    if (this.selectedDay && selectedDay.date && this.selectedDay.getTime() === selectedDay.date.getTime()) {
      this.selectedDay = null;
      this.tasksForSelectedDay = null;
      this.startOfWeekIndex = null;
    } else {
      this.selectedDay = selectedDay.date;
      this.startOfWeekIndex = this.days.findIndex(day => day.date && day.date.getTime() === selectedDay.date.getTime());
      this.updateTasksForSelectedDay();
    }
  }

  private updateTasksForSelectedDay(): void {
    if (this.selectedDay && this.tasks) {
      const filtered = this.tasks.filter(task => {
        const dueDate = moment(task.dueDate);
        return dueDate.date() === this.selectedDay!.getDate() &&
          dueDate.month() === this.selectedDay!.getMonth() &&
          dueDate.year() === this.selectedDay!.getFullYear();
      });

      const sorted = _.sortBy(filtered, task => task.task);

      this.tasksForSelectedDay = sorted ?? null;
    } else {
      this.tasksForSelectedDay = null;
    }
  }

  getTaskRowStart(): number | null {
    if (this.startOfWeekIndex !== null) {
      // Calculate which row the selected day's week ends on, then add 1 for the next row
      // Divide by 7 (days in a week) to find the week number, then add 1 for the header, 
      // and another +1 to move to the row below the current week.
      return Math.ceil((this.startOfWeekIndex + 1) / 7) + 1 + 1; // Adding +1 twice: once for header, once to move below
    }
    return null;
  }

  getTaskColumnSpan(): string {
    // Span across all 7 columns of the week
    return '1 / span 7';
  }


  async updateTaskStatus(event: MouseEvent, task: any, status: string) {
    event.preventDefault();
    event.stopPropagation();
    if (this.patientService.isRecordingLocked()) {
      return;
    }

    const id = task?.id;
    if (!id) {
      return;
    }

    // Mirrors the guard on the Task List (see task-list.component.ts): Completed and
    // Cancelled drop the task out of the active list, so a stray click while scrolling
    // silently loses work someone else was waiting on. Pending is reversible — no prompt.
    if (status === 'Completed' || status === 'Cancelled') {
      const confirmed = await this.showConfirmationDialog(
        'Confirm Task Status',
        `Are you sure you want to mark this task as ${status}?`,
        task?.task ? [{ patient: task.task }] : undefined,
        'No',
        'Yes'
      );

      if (!confirmed) {
        return;
      }
    }

    this.patientTaskService.updateTask(id, { id, status })
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (result) => {
          this.showToastMessage('Task status updated successfully');
        },
        error: (error) => {
          console.error('Error updating task status: ', error);
          this.showToastMessage('There was an error updating the task status. Please try again.');
        }
      });

  }


  loadTask(task: any) {
    if (this.patientService.isRecordingLocked()) {
      return;
    }
    this.patientService.getPatientById(task.patientId)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.patientTaskService.setEditingTask(task);
        this.router.navigate(['/patients', {
          outlets: {
            patient: ['edit-task', task.id],
          }
        }]);
      }, error => {
        console.error(error);
        this.showToastMessage('There was an error loading the task for editing. Please try again.');
      });
  }

  navigateToPatientFile(event: MouseEvent, patientId: string) {
    event.preventDefault();
    event.stopPropagation();
    this.patientService.getPatientById(patientId)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.router.navigate(['/patients', {
          outlets: {
            patient: ['file', patientId],
          }
        }]);
      }, error => {
        console.error(error);
        this.showToastMessage('There was an error loading the patient file. Please try again.');
      });
  }



}
